import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  ChevronDown, ChevronRight, ChevronUp, Plus, Trash2, RefreshCw, Sparkles, Table2,
  AlignLeft, Scissors, ArrowUpToLine, Image as ImageIcon, KeyRound, Loader2, X,
  MoveVertical,
} from 'lucide-react';
import {
  getScriptDocument, addChapter, updateChapter, deleteChapter, reorderChapters,
  regenerateChapter, updateSlide, deleteSlide, splitSlide, mergeSlideUp, moveSlide,
  addKnowledgeCheck, updateKnowledgeCheck, deleteKnowledgeCheck,
  regenerateKnowledgeCheck, regenerateAnswerKeys, regenerateClosing, anchorScriptImage,
} from './api';
import QuizEditor from './QuizEditor';
import AvScriptNarration from './AvScriptNarration';

// The A/V script editor: one card per chapter, each with a Table view (structure) and a
// Narration view (writing). Both edit the same data; neither is a preview.
//
// Every mutating call carries content_rev, the revision this editor loaded. A 409 means
// someone else changed the script while this author was working, so the editor reloads
// and says so rather than overwriting their colleague.

const LABEL = 'text-[10px] uppercase tracking-wide text-[#7d8aa0]';
const INPUT = 'w-full text-xs bg-transparent border border-[#2b3a4d] rounded px-2 py-1 text-[#c7d0e0]';
const BTN = 'inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-[#2b3a4d] text-[#8a95a8] hover:bg-[#12202f] disabled:opacity-40';

function WordMeter({ actual, target }) {
  if (!target) return <span className="text-[10px] text-[#5f6b7e]">{actual} words</span>;
  const over = actual > target * 1.1;
  const under = actual < target * 0.9;
  // Orange for out of range, blue for on target. No yellow or amber in this palette.
  const tone = over || under ? 'text-[#f7a23b]' : 'text-[#5ea9ff]';
  return (
    <span className={`text-[10px] ${tone}`} title={`Target ${target} narration words`}>
      {actual} / {target} words
    </span>
  );
}

function SlideRow({ slide, slideNo, images, canApprove, onPatch, onSplit, onMerge, onDelete,
                    onPlaceImage, onDetachImage, onMove, isFirst, isLast, otherChapters,
                    detachedImages, onReattachImage }) {
  const [lines, setLines] = useState(slide.video_lines || []);
  const [moveOpen, setMoveOpen] = useState(false);
  // Escape closes the move menu. With only the trigger to click, a keyboard user could
  // open it and not get out.
  useEffect(() => {
    if (!moveOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setMoveOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [moveOpen]);
  const boilerplate = slide.kind === 'boilerplate';
  const slideImages = images.filter((i) => i.anchor_slide_id === slide.sid);

  const commitLines = (next) => {
    setLines(next);
    // Only when something actually changed. A no-op save bumps content_rev and 409s every
    // other open editor of the same script, for merely tabbing through a field.
    const before = slide.video_lines || [];
    const changed = next.length !== before.length || next.some((l, i) => l !== before[i]);
    if (!changed) return;
    // FINDING: an empty array is stripped by Rails deep_munge, so the server kept the old
    // lines and a deleted last line reappeared on reload. A sentinel keeps the intent.
    onPatch({ video_lines: next.length ? next : [''] });
  };

  return (
    <div className="grid grid-cols-[3rem_1fr_1fr] gap-2 border-t border-[#1d2634] py-2">
      <div className="text-[11px] text-[#5f6b7e] pt-1">{slideNo}</div>

      <div className="space-y-1">
        {boilerplate ? (
          <>
            <div className="text-xs text-[#8a95a8] whitespace-pre-wrap bg-[#0b0e13] border border-[#1d2634] rounded p-2">
              {slide.audio}
            </div>
            <span className="text-[10px] text-[#5f6b7e]">
              Standard ZU narration. Edit it in Admin &gt; A/V Script Text.
            </span>
          </>
        ) : (
          <textarea
            aria-label={`Narration for slide ${slideNo}`}
            defaultValue={slide.audio}
            onBlur={(e) => e.target.value !== slide.audio && onPatch({ audio: e.target.value })}
            rows={Math.min(10, Math.max(3, Math.ceil((slide.audio || '').length / 60)))}
            placeholder="What the narrator says on this slide"
            className={INPUT}
          />
        )}
        <div className="flex items-center gap-1">
          <span className="text-[10px] text-[#5f6b7e]">
            {(slide.audio || '').split(/\s+/).filter(Boolean).length} words
          </span>
          {!boilerplate && (
            <>
              <button className={BTN} onClick={() => onSplit(slide)}
                aria-label={`Split slide ${slideNo} in two`} title="Split this slide in two">
                <Scissors size={11} />
              </button>
              <button className={BTN} onClick={() => onMerge(slide)} disabled={isFirst}
                aria-label={`Merge slide ${slideNo} into the one above`}
                title={isFirst ? 'The first slide has nothing above it' : 'Merge into the slide above'}>
                <ArrowUpToLine size={11} />
              </button>
              <span className="relative">
                <button className={BTN} onClick={() => setMoveOpen(!moveOpen)}
                  aria-label={`Move slide ${slideNo}`} aria-expanded={moveOpen}
                  title="Move this slide">
                  <MoveVertical size={11} />
                </button>
                {moveOpen && (
                  <span className="absolute z-10 left-0 mt-1 w-56 rounded border border-[#2b3a4d] bg-[#0f1620] p-1 block">
                    <button className="block w-full text-left text-[11px] px-2 py-1 text-[#8a95a8] hover:bg-[#12202f] disabled:opacity-30"
                      disabled={isFirst}
                      onClick={() => { setMoveOpen(false); onMove({ delta: -1 }); }}>
                      Move up in this chapter
                    </button>
                    <button className="block w-full text-left text-[11px] px-2 py-1 text-[#8a95a8] hover:bg-[#12202f] disabled:opacity-30"
                      disabled={isLast}
                      onClick={() => { setMoveOpen(false); onMove({ delta: 1 }); }}>
                      Move down in this chapter
                    </button>
                    {otherChapters.map((ch) => (
                      <button key={ch.cid}
                        className="block w-full text-left text-[11px] px-2 py-1 text-[#8a95a8] hover:bg-[#12202f] truncate"
                        onClick={() => { setMoveOpen(false); onMove({ toCid: ch.cid }); }}>
                        Move to end of {ch.label}
                      </button>
                    ))}
                  </span>
                )}
              </span>
              <button className={BTN} onClick={() => onDelete(slide)}
                aria-label={`Delete slide ${slideNo}`} title="Delete this slide">
                <Trash2 size={11} />
              </button>
            </>
          )}
        </div>
      </div>

      <div className="space-y-1">
        <input
          aria-label={`On-screen title for slide ${slideNo}`}
          defaultValue={slide.video_title}
          onBlur={(e) => e.target.value !== slide.video_title && onPatch({ video_title: e.target.value })}
          placeholder="Slide title"
          className={`${INPUT} font-semibold`}
          disabled={boilerplate}
        />
        {lines.map((line, li) => (
          <div key={li} className="flex items-center gap-1">
            <input
              aria-label={`On-screen line ${li + 1} for slide ${slideNo}`}
              value={line}
              onChange={(e) => setLines(lines.map((l, i) => (i === li ? e.target.value : l)))}
              onBlur={() => commitLines(lines)}
              placeholder="On-screen line"
              className={INPUT}
              disabled={boilerplate}
            />
            {!boilerplate && (
              <button className="p-1 text-[#f7a23b]"
                onClick={() => commitLines(lines.filter((_, i) => i !== li))} title="Remove this line">
                <X size={11} />
              </button>
            )}
          </div>
        ))}
        {!boilerplate && (
          <button className="text-[11px] text-[#5ea9ff]" onClick={() => commitLines([...lines, ''])}>
            + on-screen line
          </button>
        )}
        <input
          aria-label={`Production note for slide ${slideNo}`}
          defaultValue={slide.visual_note}
          onBlur={(e) => e.target.value !== slide.visual_note && onPatch({ visual_note: e.target.value })}
          placeholder="Production note, for example [add the workshop from the Visuals doc]"
          className={`${INPUT} italic`}
          disabled={boilerplate}
        />
        {slideImages.length > 0 && (
          <div className="flex flex-wrap gap-1 pt-1">
            {slideImages.map((img) => (
              <div key={img.id} className="relative">
                <img src={img.url} alt={img.alt_text || ''}
                  className="h-12 w-12 object-cover rounded border border-[#2b3a4d]" />
                <button onClick={() => onDetachImage(img)}
                  className="absolute -top-1 -right-1 bg-[#0b0e13] border border-[#2b3a4d] rounded-full p-0.5 text-[#f7a23b]"
                  title="Take this image off the slide">
                  <X size={9} />
                </button>
              </div>
            ))}
          </div>
        )}
        {!boilerplate && canApprove && (
          <div className="flex flex-wrap items-center gap-2">
            <button className="text-[11px] text-[#5ea9ff] inline-flex items-center gap-1"
              onClick={() => onPlaceImage(slide)}>
              <ImageIcon size={11} /> generate an image here
            </button>
            {/* Puts an already-approved image back on a slide. Without this the detached
                tray was a dead end and an approved image had to be regenerated and
                re-approved to be usable again. */}
            {detachedImages.length > 0 && (
              <label className="text-[11px] text-[#8a95a8] inline-flex items-center gap-1">
                or reuse
                <select
                  value=""
                  aria-label={`Put an existing image on slide ${slideNo}`}
                  onChange={(e) => e.target.value && onReattachImage(slide, Number(e.target.value))}
                  className="text-[11px] bg-transparent border border-[#2b3a4d] rounded px-1 py-0.5 text-[#c7d0e0]"
                >
                  <option value="">an unplaced image…</option>
                  {detachedImages.map((img) => (
                    <option key={img.id} value={img.id}>
                      {img.caption || img.alt_text || `image ${img.id}`}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ChapterCard({ doc, chapter, index, total, numbering, measured, images, canApprove,
                      contentRev, generating, mutateFn, artifactId, onPlaceImage,
                      detachedImages }) {
  const [open, setOpen] = useState(index === 0);
  const [view, setView] = useState('table');
  // Edited narration rows, and the rev they were typed against. If the document moves
  // underneath (a colleague's edit, a regeneration landing), the rows are stale and must
  // not be saveable: saving them with the FRESH rev is exactly the write the rev
  // mechanism exists to reject.
  const [pending, setPending] = useState(null);
  const info = numbering.chapters[chapter.cid] || {};
  const words = measured.chapters?.[chapter.cid] || 0;
  const slideNo = (sid) => numbering.slides[sid] || '';

  const call = (fn) => mutateFn(fn);

  const narrationStale = pending && pending.rev !== contentRev;

  const saveNarration = () => {
    if (!pending || narrationStale) return;
    // Matched by SID, never by position. fromDoc preserves each row's sid, and a row with
    // none is a slide the author split off. Index pairing silently moved one slide's
    // narration onto another as soon as a boundary was deleted.
    const slides = pending.rows.map((row) => {
      const existing = (chapter.slides || []).find((s) => s.sid === row.sid);
      return {
        ...(existing || {}),
        sid: row.sid || undefined,
        audio: row.audio,
        reveal_marks: row.reveal_marks,
      };
    });
    call(() => updateChapter(artifactId, chapter.cid, { slides, content_rev: contentRev }));
    setPending(null);
  };

  // The document moved underneath, so what is on screen is no longer what was typed
  // against. Drop the rows rather than letting them be saved over someone else's work.
  useEffect(() => {
    if (pending && pending.rev !== contentRev) setPending(null);
  }, [contentRev]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="border border-[#2b3a4d] rounded-lg bg-[#0b0e13]">
      <div className="flex items-center gap-2 px-3 py-2">
        <button onClick={() => setOpen(!open)} className="text-[#8a95a8]">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        <span className="text-[11px] font-semibold text-[#c7d0e0] whitespace-nowrap">
          CHAPTER {info.number}
        </span>
        <input
          aria-label={`Chapter ${info.number} name`}
          defaultValue={chapter.name}
          onBlur={(e) => e.target.value !== chapter.name
            && call(() => updateChapter(artifactId, chapter.cid, { name: e.target.value, content_rev: contentRev }))}
          className="flex-1 min-w-0 text-xs bg-transparent border-b border-transparent hover:border-[#2b3a4d] focus:border-[#2b3a4d] text-[#c7d0e0] px-1"
        />
        <span className="text-[10px] text-[#5f6b7e] whitespace-nowrap">part {chapter.part}</span>
        <WordMeter actual={words} target={chapter.word_target} />
        {info.kc_number && (
          <span className="text-[10px] text-[#5ea9ff] whitespace-nowrap">check #{info.kc_number}</span>
        )}
        {generating && <Loader2 size={13} className="animate-spin text-[#5ea9ff]" />}
        <div className="flex items-center gap-1">
          <button className={BTN} disabled={index === 0}
            onClick={() => call(() => reorderChapters(artifactId, {
              cids: moveCid(doc, chapter.cid, -1), content_rev: contentRev,
            }))} aria-label={`Move chapter ${info.number} up`}
            title="Move this chapter up"><ChevronUp size={11} /></button>
          <button className={BTN} disabled={index === total - 1}
            onClick={() => call(() => reorderChapters(artifactId, {
              cids: moveCid(doc, chapter.cid, 1), content_rev: contentRev,
            }))} aria-label={`Move chapter ${info.number} down`}
            title="Move this chapter down"><ChevronDown size={11} /></button>
          {canApprove && (
            <button className={BTN} disabled={generating}
              onClick={() => {
                const instruction = window.prompt(
                  'Optional: what should change? Leave blank to write this chapter again from scratch.',
                  '');
                if (instruction === null) return;
                call(() => regenerateChapter(artifactId, chapter.cid, {
                  instruction: instruction || null, content_rev: contentRev,
                }));
              }}
              aria-label={`Rewrite chapter ${info.number} with JPRO Intelligence`}
              title="Rewrite this chapter with JPRO Intelligence">
              <RefreshCw size={11} />
            </button>
          )}
          <button className={BTN}
            onClick={() => {
              if (!window.confirm(`Delete chapter ${info.number}, "${chapter.name}"?`)) return;
              call(() => deleteChapter(artifactId, chapter.cid, contentRev));
            }} aria-label={`Delete chapter ${info.number}`}
            title="Delete this chapter"><Trash2 size={11} /></button>
        </div>
      </div>

      {open && (
        <div className="px-3 pb-3 space-y-2">
          <div className="flex items-center gap-1">
            <button onClick={() => setView('table')}
              className={`${BTN} ${view === 'table' ? 'text-[#5ea9ff] border-[#5ea9ff]' : ''}`}>
              <Table2 size={11} /> Table
            </button>
            <button onClick={() => setView('narration')}
              className={`${BTN} ${view === 'narration' ? 'text-[#5ea9ff] border-[#5ea9ff]' : ''}`}>
              <AlignLeft size={11} /> Narration
            </button>
            <input
              aria-label={`What chapter ${info.number} accomplishes`}
              defaultValue={chapter.purpose}
              onBlur={(e) => e.target.value !== chapter.purpose
                && call(() => updateChapter(artifactId, chapter.cid, { purpose: e.target.value, content_rev: contentRev }))}
              placeholder="What this chapter accomplishes"
              className={`${INPUT} flex-1`}
            />
          </div>

          {view === 'table' ? (
            <div>
              <div className="grid grid-cols-[3rem_1fr_1fr] gap-2 pb-1">
                <span className={LABEL}>Slide</span>
                <span className={LABEL}>Audio</span>
                <span className={LABEL}>Video</span>
              </div>
              {(chapter.slides || []).map((slide, si) => (
                <SlideRow
                  // Includes the revision so a reload after a 409, or a regeneration
                  // landing, remounts the row with the server's values rather than leaving
                  // stale text in an uncontrolled input.
                  key={`${slide.sid}-${contentRev}`}
                  slide={slide}
                  slideNo={slideNo(slide.sid)}
                  images={images}
                  canApprove={canApprove}
                  isFirst={si === 0}
                  isLast={si === (chapter.slides || []).length - 1}
                  otherChapters={(doc.chapters || [])
                    .filter((ch) => ch.cid !== chapter.cid)
                    .map((ch) => ({
                      cid: ch.cid,
                      label: `Chapter ${numbering.chapters[ch.cid]?.number}: ${ch.name}`,
                    }))}
                  onMove={({ delta, toCid }) => call(() => moveSlide(
                    artifactId, chapter.cid, slide.sid,
                    // Same chapter with a new index reorders; a different cid moves it
                    // across and lands it at the end.
                    toCid
                      ? { to_cid: toCid, to_index: 9999, content_rev: contentRev }
                      : { to_cid: chapter.cid, to_index: si + delta, content_rev: contentRev },
                  ))}
                  onPatch={(attrs) => call(() => updateSlide(artifactId, chapter.cid, slide.sid,
                    { ...attrs, content_rev: contentRev }))}
                  onSplit={() => {
                    const at = window.prompt('Split after how many characters of narration?',
                                             String(Math.floor((slide.audio || '').length / 2)));
                    if (at === null) return;
                    call(() => splitSlide(artifactId, chapter.cid, slide.sid,
                                          { at_char: Number(at) || 0, content_rev: contentRev }));
                  }}
                  onMerge={() => call(() => mergeSlideUp(artifactId, chapter.cid, slide.sid,
                                                         { content_rev: contentRev }))}
                  onDelete={() => {
                    if (!window.confirm('Delete this slide? Any images on it move to the tray.')) return;
                    call(() => deleteSlide(artifactId, chapter.cid, slide.sid, contentRev));
                  }}
                  detachedImages={detachedImages}
                  onReattachImage={(target, imageId) => call(() => anchorScriptImage(
                    artifactId, imageId,
                    { cid: chapter.cid, sid: target.sid, content_rev: contentRev },
                  ))}
                  onPlaceImage={() => onPlaceImage({ cid: chapter.cid, sid: slide.sid })}
                  onDetachImage={(img) => call(() => anchorScriptImage(artifactId, img.id,
                                                                      { sid: null, content_rev: contentRev }))}
                />
              ))}
              {chapter.part_conclusion && (
                <div className="border-t border-[#1d2634] pt-2 mt-1">
                  <span className={LABEL}>Part conclusion</span>
                  <p className="text-xs text-[#8a95a8] whitespace-pre-wrap">
                    {chapter.part_conclusion.audio}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <AvScriptNarration
                chapter={{ ...chapter, content_rev: contentRev }}
                numbering={numbering.slides}
                onChange={(rows) => setPending({ rows, rev: contentRev })}
                onSplit={({ sid, atChar }) => {
                  if (pending) { toast.error('Save the narration first, then split.'); return; }
                  if (!sid) return;
                  call(() => splitSlide(artifactId, chapter.cid, sid,
                                        { at_char: atChar, content_rev: contentRev }));
                }}
                onMergeUp={({ sid }) => {
                  if (pending) { toast.error('Save the narration first, then merge.'); return; }
                  const slides = chapter.slides || [];
                  const index = slides.findIndex((s) => s.sid === sid);
                  if (index <= 0) { toast.error('The first slide has nothing above it.'); return; }
                  call(() => mergeSlideUp(artifactId, chapter.cid, sid, { content_rev: contentRev }));
                }}
              />
              <div className="flex items-center gap-2">
                <button className="text-xs px-3 py-1.5 rounded-lg bg-[#1f6feb] text-white disabled:opacity-40"
                  disabled={!pending || narrationStale} onClick={saveNarration}>
                  Save narration
                </button>
                {pending && !narrationStale && (
                  <span className="text-[10px] text-[#f7a23b]">unsaved narration</span>
                )}
              </div>
              {/* Split and merge act on the SAVED text, so unsaved typing has to go first
                  or the server would cut the wrong string and the reload would discard it. */}
              <p className="text-[10px] text-[#5f6b7e]">
                Save before splitting or merging a slide.
              </p>
            </div>
          )}

          {/* Knowledge check. Same editor as a quiz, not a lookalike. */}
          <div className="border-t border-[#1d2634] pt-2">
            {chapter.knowledge_check ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className={LABEL}>Knowledge check #{info.kc_number}</span>
                  {canApprove && (
                    <>
                      <button className={BTN} disabled={generating}
                        onClick={() => call(() => regenerateKnowledgeCheck(artifactId, chapter.cid,
                                                                          { content_rev: contentRev }))}
                        title="Write new questions with JPRO Intelligence">
                        <Sparkles size={11} /> new questions
                      </button>
                      <button className={BTN} disabled={generating}
                        onClick={() => call(() => regenerateAnswerKeys(artifactId, chapter.cid,
                                                                      { content_rev: contentRev }))}
                        title="Keep these questions, recheck the answer key">
                        <KeyRound size={11} /> recheck answers
                      </button>
                    </>
                  )}
                  <button className={BTN}
                    onClick={() => {
                      if (!window.confirm('Remove this knowledge check?')) return;
                      call(() => deleteKnowledgeCheck(artifactId, chapter.cid, contentRev));
                    }}><Trash2 size={11} /> remove</button>
                </div>
                <QuizEditor
                  key={`${chapter.cid}-${contentRev}`}
                  value={chapter.knowledge_check.questions}
                  explanationKey="response"
                  explanationLabel="Response (shown when the answer is revealed)"
                  explanationPlaceholder="What the learner is told when the answer appears"
                  defaultChoiceCount={4}
                  footerNote="Saving updates the script."
                  onSave={(questions) => call(() => updateKnowledgeCheck(artifactId, chapter.cid,
                                                                        { questions, content_rev: contentRev }))}
                  onCancel={() => {}}
                />
              </div>
            ) : (
              <button className={BTN}
                onClick={() => call(() => addKnowledgeCheck(artifactId, chapter.cid,
                                                            { content_rev: contentRev }))}>
                <Plus size={11} /> add a knowledge check
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// The full chapter order with one cid moved, which is what the reorder endpoint wants.
function moveCid(doc, cid, delta) {
  const cids = (doc.chapters || []).map((c) => c.cid);
  const from = cids.indexOf(cid);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= cids.length) return cids;
  const next = cids.slice();
  next.splice(to, 0, next.splice(from, 1)[0]);
  return next;
}

export default function AvScriptEditor({ artifactId, canApprove, onPlaceImage }) {
  const qc = useQueryClient();
  const key = ['av-script', artifactId];
  const { data, isLoading, error } = useQuery({
    queryKey: key,
    queryFn: () => getScriptDocument(artifactId),
    // A chapter regenerating in the background lands here, so poll while one is.
    refetchInterval: (query) => (query.state.data?.generating_cids?.length ? 4000 : false),
  });

  const mutation = useMutation({
    mutationFn: (fn) => fn(),
    onSuccess: (payload) => {
      qc.setQueryData(key, payload);
      if (payload?.enqueued === false && payload?.ran_inline) {
        toast.success('Generated now, no background worker was running.');
      }
    },
    onError: (e) => {
      // A 409 is not a failure to explain away: someone else's work is in the document
      // and this editor is out of date.
      if (e?.status === 409 || /changed this script/i.test(e?.message || '')) {
        qc.invalidateQueries({ queryKey: key });
        toast.error('Someone else changed this script. Reloaded it, please reapply your change.');
        return;
      }
      toast.error(e?.message || 'That change did not save');
    },
  });

  if (isLoading) return <p className="text-xs text-[#5f6b7e]">Loading the script…</p>;
  if (error) return <p className="text-xs text-[#f7a23b]">{error.message || 'Could not load the script'}</p>;

  const doc = data.document || {};
  const contentRev = data.artifact?.content_rev;
  const generatingCids = data.generating_cids || [];
  const stage = data.stage || {};
  const runtimeTarget = data.budget?.total_words;

  return (
    <div className="space-y-3">
      {/* Front matter */}
      <div className="border border-[#2b3a4d] rounded-lg bg-[#0b0e13] p-3 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <span className={LABEL}>Class title and description</span>
          {canApprove && (
            <button className={BTN}
              onClick={() => mutation.mutate(() => regenerateClosing(artifactId, { content_rev: contentRev }))}
              title="Rewrite the description, objectives, pronunciation key and glossary">
              <Sparkles size={11} /> redo front matter
            </button>
          )}
        </div>
        <p className="text-xs text-[#c7d0e0]">
          {doc.front_matter?.pathway_name ? `${doc.front_matter.pathway_name} - ` : ''}
          {doc.front_matter?.class_name}
        </p>
        <p className="text-xs text-[#8a95a8] whitespace-pre-wrap">{doc.front_matter?.description}</p>
        {(doc.front_matter?.objectives || []).length > 0 && (
          <ul className="list-disc pl-5 text-xs text-[#8a95a8]">
            {doc.front_matter.objectives.map((o, i) => <li key={i}>{o}</li>)}
          </ul>
        )}
        <div className="flex flex-wrap gap-3 text-[10px] text-[#5f6b7e]">
          {doc.front_matter?.minutes && <span>Time: approximately {doc.front_matter.minutes} minutes</span>}
          {doc.front_matter?.level && <span>Level: {doc.front_matter.level}</span>}
          {(doc.front_matter?.audience || []).length > 0
            && <span>Audience: {doc.front_matter.audience.join(', ')}</span>}
          <span>{doc.chapters?.length || 0} chapters</span>
        </div>
      </div>

      {/* Budget line */}
      <div className="flex items-center gap-3 flex-wrap text-[11px]">
        <span className="text-[#c7d0e0]">
          {data.measured?.total_words} words
          {runtimeTarget ? ` of ${runtimeTarget}` : ''}
        </span>
        <span className="text-[#8a95a8]">
          {data.measured?.runtime_minutes} min
          {doc.front_matter?.minutes ? ` of ${doc.front_matter.minutes}` : ''}
          {' '}at {data.measured?.wpm} wpm
        </span>
        {stage.stage && stage.chapter_count > 0 && stage.chapter_index < stage.chapter_count && (
          <span className="inline-flex items-center gap-1 text-[#5ea9ff]">
            <Loader2 size={11} className="animate-spin" />
            chapter {stage.chapter_index} of {stage.chapter_count}
          </span>
        )}
        {data.generation_error && <span className="text-[#f7a23b]">{data.generation_error}</span>}
      </div>

      {/* Images that lost their slide. Surfaced rather than silently dropped. */}
      {(data.detached_images || []).length > 0 && (
        <div className="border border-[#f7a23b]/40 rounded-lg bg-[#0b0e13] p-3">
          <span className={LABEL}>
            Images with no slide ({data.detached_images.length})
          </span>
          <p className="text-[10px] text-[#8a95a8] mb-2">
            These were on slides that have since been regenerated or deleted. They keep their
            approval: use "or reuse an unplaced image" on any slide to put one back without
            re-approving it.
          </p>
          <div className="flex flex-wrap gap-2">
            {data.detached_images.map((img) => (
              <img key={img.id} src={img.url} alt={img.alt_text || ''}
                title={img.caption || 'Use "place an image here" on a slide'}
                className="h-14 w-14 object-cover rounded border border-[#2b3a4d]" />
            ))}
          </div>
        </div>
      )}

      {(doc.chapters || []).map((chapter, index) => (
        <ChapterCard
          key={chapter.cid}
          doc={doc}
          chapter={chapter}
          index={index}
          total={doc.chapters.length}
          numbering={data.numbering}
          measured={data.measured}
          images={data.images || []}
          canApprove={canApprove}
          contentRev={contentRev}
          generating={generatingCids.includes(chapter.cid)}
          detachedImages={data.detached_images || []}
          mutateFn={(fn) => mutation.mutate(fn)}
          artifactId={artifactId}
          onPlaceImage={onPlaceImage}
        />
      ))}

      <button className={BTN}
        onClick={() => {
          const name = window.prompt('What is this chapter called?', '');
          if (!name) return;
          mutation.mutate(() => addChapter(artifactId, { name, content_rev: contentRev }));
        }}>
        <Plus size={11} /> add a chapter
      </button>
    </div>
  );
}
